import React, { useState } from 'react';
import { Mail, Linkedin, Github, Instagram, MessageCircle, Heart, Coffee, Code, Copy, Check } from 'lucide-react';
import { socialLinks } from '../../mock/data';
import './Contact.css';

const Contact = ({ language }) => {
  const [copiedEmail, setCopiedEmail] = useState(false);

  const contactContent = {
    pt: {
      title: 'Conecte-se Comigo',
      subtitle: 'Vamos conversar sobre tecnologia, projetos ou oportunidades de colaboração',
      socialTitle: 'Redes Sociais',
      socialSubtitle: 'Me encontre nas principais plataformas',
      emailTitle: 'Email Direto',
      emailSubtitle: 'Para conversas mais formais',
      coffeeTitle: 'Vamos tomar um café?',
      coffeeSubtitle: 'Sempre aberto para networking e novas ideias',
      coffeeText: 'Se você está em Belo Horizonte ou região, seria ótimo conversar pessoalmente sobre tecnologia, projetos ou simplesmente trocar experiências!',
      availability: {
        title: 'Disponibilidade',
        status: 'Disponível para novas oportunidades',
        response: 'Respondo em até 24 horas',
        location: 'Belo Horizonte, MG'
      },
      cta: {
        title: 'Pronto para colaborar?',
        description: 'Estou sempre interessado em projetos desafiadores e oportunidades de crescimento.',
        email: 'Enviar Email',
        linkedin: 'Conectar no LinkedIn',
        copyEmail: 'Copiar Email',
        emailCopied: 'Email Copiado!',
        openGmail: 'Abrir no Gmail',
        chooseOption: 'Escolha uma opção'
      }
    },
    en: {
      title: 'Connect With Me',
      subtitle: 'Let\'s talk about technology, projects or collaboration opportunities',
      socialTitle: 'Social Media',
      socialSubtitle: 'Find me on major platforms',
      emailTitle: 'Direct Email',
      emailSubtitle: 'For more formal conversations',
      coffeeTitle: 'Let\'s have coffee?',
      coffeeSubtitle: 'Always open for networking and new ideas',
      coffeeText: 'If you\'re in Belo Horizonte or the region, it would be great to chat in person about technology, projects, or simply exchange experiences!',
      availability: {
        title: 'Availability',
        status: 'Available for new opportunities',
        response: 'I respond within 24 hours',
        location: 'Belo Horizonte, MG'
      },
      cta: {
        title: 'Ready to collaborate?',
        description: 'I\'m always interested in challenging projects and growth opportunities.',
        email: 'Send Email',
        linkedin: 'Connect on LinkedIn',
        copyEmail: 'Copy Email',
        emailCopied: 'Email Copied!',
        openGmail: 'Open Gmail',
        chooseOption: 'Choose an option'
      }
    }
  };

  const content = contactContent[language];

  const EMAIL = 'arthur.pazzutti0@gmail.com';

  // Copiar email para clipboard
  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 3000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = EMAIL;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 3000);
    }
  };

  // Abrir Gmail Web diretamente
  const handleEmailClick = () => {
    const subject = encodeURIComponent("Contato do Portfólio");
    const body = encodeURIComponent("Olá Pazzutti, vi seu portfólio e gostaria de conversar sobre...");
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${EMAIL}&su=${subject}&body=${body}`, '_blank');
  };

  const getIconComponent = (iconName) => {
    const icons = {
      'Mail': Mail,
      'Linkedin': Linkedin,
      'Github': Github,
      'Instagram': Instagram
    };
    return icons[iconName] || Mail;
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="contact-header">
          <h2 className="section-title">{content.title}</h2>
          <p className="section-subtitle">{content.subtitle}</p>
        </div>

        <div className="contact-content">
          {/* Social Links Section */}
          <div className="social-section">
            <div className="section-header">
              <h3 className="section-title-small">{content.socialTitle}</h3>
              <p className="section-description">{content.socialSubtitle}</p>
            </div>

            <div className="social-grid">
              {socialLinks.map((link, index) => {
                const IconComponent = getIconComponent(link.icon);
                const isEmail = link.icon === 'Mail';
                
                if (isEmail) {
                  return (
                    <div
                      key={index}
                      className="social-card email-social-card"
                      style={{ '--social-color': link.color }}
                      onClick={handleEmailClick}
                    >
                      <div className="social-icon">
                        <IconComponent size={24} />
                      </div>
                      <div className="social-info">
                        <h4 className="social-name">{link.name}</h4>
                        <span className="social-handle">{link.url}</span>
                      </div>
                      <div className="social-arrow">
                        <Mail size={20} />
                      </div>
                    </div>
                  );
                }
                
                return (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-card"
                    style={{ '--social-color': link.color }}
                  >
                    <div className="social-icon">
                      <IconComponent size={24} />
                    </div>
                    <div className="social-info">
                      <h4 className="social-name">{link.name}</h4>
                      <span className="social-handle">Arthur Pazzutti</span>
                    </div>
                    <div className="social-arrow">→</div>
                  </a>
                );
              })}
            </div>
          </div>

          {/* Direct Contact Section */}
          <div className="direct-contact">
            <div className="contact-card email-card">
              <div className="card-icon">
                <Mail size={32} />
              </div>
              <div className="card-content">
                <h3 className="card-title">{content.emailTitle}</h3>
                <p className="card-description">{content.emailSubtitle}</p>
                <div className="email-display">
                  <code className="email-text">{EMAIL}</code>
                  <button className="btn-copy-inline" onClick={handleCopyEmail} title={content.cta.copyEmail}>
                    {copiedEmail ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                
                <button className="btn-primary" onClick={handleEmailClick}>
                  <Mail size={16} />
                  {content.cta.email}
                </button>
              </div>
            </div>

            <div className="contact-card coffee-card">
              <div className="card-icon coffee-icon">
                <Coffee size={32} />
              </div>
              <div className="card-content">
                <h3 className="card-title">{content.coffeeTitle}</h3>
                <p className="card-description">{content.coffeeSubtitle}</p>
                <p className="coffee-text">{content.coffeeText}</p>
                <div className="coffee-stats">
                  <div className="coffee-stat">
                    <Heart size={16} />
                    <span>{language === 'pt' ? 'Networking' : 'Networking'}</span>
                  </div>
                  <div className="coffee-stat">
                    <Code size={16} />
                    <span>{language === 'pt' ? 'Tech Talks' : 'Tech Talks'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Availability Info */}
          <div className="availability-section">
            <div className="availability-card">
              <div className="availability-header">
                <div className="status-indicator">
                  <div className="status-dot"></div>
                  <h3 className="availability-title">{content.availability.title}</h3>
                </div>
              </div>

              <div className="availability-info">
                <div className="availability-item">
                  <MessageCircle size={20} />
                  <span>{content.availability.status}</span>
                </div>
                <div className="availability-item">
                  <Mail size={20} />
                  <span>{content.availability.response}</span>
                </div>
                <div className="availability-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  <span>{content.availability.location}</span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="contact-cta">
            <div className="cta-background">
              <div className="cta-pattern"></div>
            </div>
            <div className="cta-content">
              <h3 className="cta-title">{content.cta.title}</h3>
              <p className="cta-description">{content.cta.description}</p>
              
              <div className="cta-buttons">
                <button className="btn-primary" onClick={handleEmailClick}>
                  <Mail size={16} />
                  {content.cta.email}
                </button>
                <a
                  href="https://www.linkedin.com/in/arthur-pazzutti-635ba4330/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  <Linkedin size={16} />
                  {content.cta.linkedin}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;